import React, { useState, useEffect, useRef } from 'react';
import { settingsAPI } from '../utils/api';

// Small role badge used inside the user picker.
function RoleTag({ role, display }) {
  const cls = {
    sysadmin: 'bg-purple-100 text-purple-700',
    admin: 'bg-blue-100 text-blue-700',
    client: 'bg-gray-100 text-gray-700',
    contractor: 'bg-green-100 text-green-700',
    dre: 'bg-amber-100 text-amber-700',
  }[role] || 'bg-gray-100 text-gray-700';
  return (
    <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded ${cls}`}>
      {display || role}
    </span>
  );
}

/**
 * Header control shown to a real System Administrator (not while impersonating).
 * Opens a searchable list of users; picking one starts impersonation.
 */
export function SwitchUserMenu({ currentUserId, onImpersonate }) {
  const [open, setOpen] = useState(false);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [busyId, setBusyId] = useState(null);
  const ref = useRef(null);

  // Close on outside click.
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  const loadUsers = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await settingsAPI.getUsers();
      setUsers(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      setError('Could not load users.');
    } finally {
      setLoading(false);
    }
  };

  const toggle = () => {
    const next = !open;
    setOpen(next);
    if (next && users.length === 0) loadUsers();
  };

  const handlePick = async (user) => {
    setBusyId(user.id);
    setError('');
    try {
      await onImpersonate(user.id);
      setOpen(false);
      setSearch('');
    } catch (err) {
      setError(err?.response?.data?.error || 'Failed to switch user.');
    } finally {
      setBusyId(null);
    }
  };

  const term = search.trim().toLowerCase();
  const visible = users
    .filter((u) => String(u.id) !== String(currentUserId))
    .filter((u) =>
      !term ||
      u.username?.toLowerCase().includes(term) ||
      u.email?.toLowerCase().includes(term) ||
      u.role?.toLowerCase().includes(term)
    );

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={toggle}
        title="Switch to another user (impersonate)"
        aria-label="Switch user"
        className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-sm text-gray-600 hover:bg-gray-100 border border-gray-200"
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
            d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
        </svg>
        <span className="hidden sm:inline">Switch user</span>
        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {open && (
        <div className="absolute right-0 mt-2 w-72 bg-white rounded-lg shadow-lg border border-gray-200 z-50">
          <div className="p-2 border-b border-gray-100">
            <p className="text-xs font-semibold text-gray-500 px-1 pb-1.5">Impersonate a user</p>
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search name, email or role…"
              autoFocus
              className="w-full text-sm px-2.5 py-1.5 border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-primary-500"
            />
          </div>

          <div className="max-h-72 overflow-y-auto py-1">
            {loading && <p className="px-3 py-4 text-sm text-gray-400 text-center">Loading users…</p>}
            {error && <p className="px-3 py-2 text-sm text-red-600">{error}</p>}
            {!loading && !error && visible.length === 0 && (
              <p className="px-3 py-4 text-sm text-gray-400 text-center">No matching users.</p>
            )}
            {!loading && visible.map((u) => (
              <button
                key={u.id}
                onClick={() => handlePick(u)}
                disabled={busyId !== null}
                className="w-full flex items-center justify-between gap-2 px-3 py-2 text-left hover:bg-gray-50 disabled:opacity-50"
              >
                <span className="min-w-0">
                  <span className="block text-sm text-gray-800 truncate">
                    {u.username}
                    {u.suspended && <span className="ml-1.5 text-[10px] text-red-600 font-medium">(suspended)</span>}
                  </span>
                  {u.email && <span className="block text-xs text-gray-400 truncate">{u.email}</span>}
                </span>
                <span className="flex items-center gap-1.5 shrink-0">
                  <RoleTag role={u.role} display={u.roleDisplay} />
                  {busyId === u.id && (
                    <svg className="w-3.5 h-3.5 animate-spin text-gray-400" viewBox="0 0 24 24" fill="none">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.4 0 0 5.4 0 12h4z" />
                    </svg>
                  )}
                </span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Full-width banner shown while a System Administrator is impersonating.
 */
export function ImpersonationBanner({ targetUsername, targetRole, impersonatorUsername, onStop }) {
  const [busy, setBusy] = useState(false);

  const handleStop = async () => {
    setBusy(true);
    try {
      await onStop();
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="bg-amber-500 text-white">
      <div className="px-4 py-2 sm:px-6 lg:px-8 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 min-w-0">
          <svg className="w-4 h-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
              d="M15 12a3 3 0 11-6 0 3 3 0 016 0z M2.5 12S5 5 12 5s9.5 7 9.5 7-2.5 7-9.5 7-9.5-7-9.5-7z" />
          </svg>
          <p className="text-sm truncate">
            Viewing as <span className="font-semibold">{targetUsername}</span>
            {targetRole && <span className="opacity-90"> ({targetRole})</span>}
            <span className="hidden sm:inline opacity-90"> — you are signed in as {impersonatorUsername}</span>
          </p>
        </div>
        <button
          onClick={handleStop}
          disabled={busy}
          className="shrink-0 flex items-center gap-1.5 bg-white/20 hover:bg-white/30 disabled:opacity-60 text-white text-sm font-medium px-3 py-1 rounded-md"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
          </svg>
          {busy ? 'Returning…' : 'Return to my account'}
        </button>
      </div>
    </div>
  );
}
